import { useState, useEffect } from 'react'

interface TextViewerDialogProps {
  content: string
  fileName?: string
  fontSize?: number
  fontFamily?: string
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
  hasPrev?: boolean
  hasNext?: boolean
  currentIndex?: number
  totalCount?: number
  allowDownload?: boolean
  onSave?: (content: string, fontSize: number, fontFamily: string) => void
  initialRotation?: number // 初始旋转角度（0, 90, 180, 270）
  onRotationChange?: (rotation: number) => void // 旋转变化回调
  // 分享链接相关
  shareLink?: string // 当前文本的分享链接
  onShareLinkChange?: (link: string) => void | Promise<void> // 分享链接变化回调
  onOpenLinkSelector?: () => void // 打开链接选择对话框的回调
  showShareLinkInput?: boolean // 是否显示分享链接输入框（编辑模式）
  shareLinkNotLoggedInMessage?: string // 未登录时的提示信息
}

// 字体列表（与设置弹窗一致）
const fontOptions = [
  { name: '微软雅黑', value: 'Microsoft YaHei, 微软雅黑, Arial, sans-serif' },
  { name: '宋体', value: 'SimSun, 宋体, serif' },
  { name: '黑体', value: 'SimHei, 黑体, Arial, sans-serif' },
  { name: '华文中宋', value: 'STZhongsong, 华文中宋, SimSun, serif' },
  { name: '华文楷体', value: 'STKaiti, 华文楷体, KaiTi, serif' },
  { name: '华文仿宋', value: 'STFangsong, 华文仿宋, FangSong, serif' },
  { name: '楷体', value: 'KaiTi, 楷体, serif' },
  { name: '仿宋', value: 'FangSong, 仿宋, serif' },
  { name: 'Arial', value: 'Arial, sans-serif' },
  { name: 'Times New Roman', value: 'Times New Roman, serif' },
  { name: 'Courier New', value: 'Courier New, monospace' },
  { name: 'Verdana', value: 'Verdana, sans-serif' },
  { name: 'Georgia', value: 'Georgia, serif' },
  { name: 'Trebuchet MS', value: 'Trebuchet MS, sans-serif' },
  { name: 'Comic Sans MS', value: 'Comic Sans MS, cursive' }
]

/**
 * 文本查看器组件
 * 支持：查看、编辑、下载、字体和字体大小设置
 */
export const TextViewerDialog = ({
  content,
  fileName,
  fontSize = 14,
  fontFamily = 'Microsoft YaHei, 微软雅黑, Arial, sans-serif',
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  currentIndex,
  totalCount,
  allowDownload = true,
  onSave,
  initialRotation = 0,
  onRotationChange,
  shareLink = '',
  onShareLinkChange,
  onOpenLinkSelector,
  showShareLinkInput = false,
  shareLinkNotLoggedInMessage
}: TextViewerDialogProps) => {
  const [textContent, setTextContent] = useState('')
  const [currentFontSize, setCurrentFontSize] = useState(fontSize)
  const [currentFontFamily, setCurrentFontFamily] = useState(fontFamily)
  const [isEditing, setIsEditing] = useState(false)
  const [rotation, setRotation] = useState(initialRotation)
  const [showLinkPanel, setShowLinkPanel] = useState(false)
  const [linkInput, setLinkInput] = useState(shareLink)

  useEffect(() => {
    // 如果是 HTML 内容，提取纯文本
    if (content.includes('<')) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = content
      setTextContent(tempDiv.textContent || tempDiv.innerText || '')
    } else {
      setTextContent(content)
    }
    setCurrentFontSize(fontSize)
    setCurrentFontFamily(fontFamily)
    setRotation(initialRotation)
    setLinkInput(shareLink)
    setShowLinkPanel(false)
  }, [content, fontSize, fontFamily, initialRotation, shareLink])

  // 键盘快捷键
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditing) {
          setIsEditing(false)
        } else {
          onClose()
        }
      } else if (e.key === 'ArrowLeft' && hasPrev && onPrev && !isEditing) {
        e.preventDefault()
        onPrev()
      } else if (e.key === 'ArrowRight' && hasNext && onNext && !isEditing) {
        e.preventDefault()
        onNext()
      } else if (e.key === 's' && (e.ctrlKey || e.metaKey) && isEditing) {
        e.preventDefault()
        handleSave()
      } else if (e.key === 't' || e.key === 'T') {
        e.preventDefault()
        handleRotate()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, onPrev, onNext, hasPrev, hasNext, isEditing])

  const handleSave = () => {
    if (onSave) {
      onSave(textContent, currentFontSize, currentFontFamily)
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    // 恢复原始内容
    if (content.includes('<')) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = content
      setTextContent(tempDiv.textContent || tempDiv.innerText || '')
    } else {
      setTextContent(content)
    }
    setCurrentFontSize(fontSize)
    setCurrentFontFamily(fontFamily)
    setIsEditing(false)
  }

  const handleRotate = () => {
    setRotation(prev => {
      const newRotation = (prev + 90) % 360
      // 调用回调函数保存旋转信息
      if (onRotationChange) {
        onRotationChange(newRotation)
      }
      return newRotation
    })
  }

  const handleDownload = () => {
    try {
      const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName || 'text.txt'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('下载文本失败:', error)
    }
  }

  // 保存分享链接
  const handleSaveShareLink = async () => {
    if (onShareLinkChange) {
      try {
        await onShareLinkChange(linkInput.trim())
        setShowLinkPanel(false)
      } catch (error) {
        // 如果保存失败，不关闭面板，让用户重试
        console.error('保存链接失败:', error)
      }
    }
  }

  // 清除分享链接
  const handleClearShareLink = () => {
    setLinkInput('')
    if (onShareLinkChange) {
      onShareLinkChange('')
    }
    setShowLinkPanel(false)
  }

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 切换按钮 - 左侧 */}
      {hasPrev && onPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onPrev()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onPrev()
          }}
          className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="上一个 (←)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
      )}

      {/* 切换按钮 - 右侧 */}
      {hasNext && onNext && (
        <button
          onClick={(e) => {
            e.stopPropagation()
            onNext()
          }}
          onTouchEnd={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onNext()
          }}
          className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white bg-opacity-80 hover:bg-opacity-100 active:bg-opacity-100 shadow-lg text-gray-700 hover:text-gray-900 transition-all touch-manipulation"
          title="下一个 (→)"
        >
          <svg width="20" height="20" className="sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      )}

      <div
        className="bg-white rounded-lg shadow-2xl w-[95vw] sm:w-[90vw] max-w-4xl h-[90vh] sm:h-[85vh] max-h-[800px] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >

        {/* 头部 */}
        <div className="flex-shrink-0 px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mb-3 sm:mb-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 truncate">查看文本</h3>
              {fileName && (
                <p className="text-xs sm:text-sm text-gray-500 mt-1 truncate">{fileName}</p>
              )}
              {currentIndex !== undefined && totalCount !== undefined && (
                <p className="text-[10px] sm:text-xs text-gray-400 mt-1">
                  {currentIndex + 1} / {totalCount}
                </p>
              )}
            </div>
            <div className="flex gap-1.5 sm:gap-2 flex-wrap">
              {!isEditing && (
                <button
                  onClick={handleRotate}
                  className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200 rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                  title="旋转 (T)"
                >
                  <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="23 4 23 10 17 10"></polyline>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                  </svg>
                  <span className="hidden sm:inline">旋转</span>
                </button>
              )}
              {!isEditing && onSave && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-green-600 text-white hover:bg-green-700 active:bg-green-700 rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                  title="编辑文本"
                >
                  <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                  </svg>
                  <span className="hidden sm:inline">编辑</span>
                </button>
              )}
              {allowDownload && (
                <button
                  onClick={handleDownload}
                  className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-700 rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                  title="下载文本"
                >
                  <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  <span className="hidden sm:inline">下载</span>
                </button>
              )}
              {showShareLinkInput && !isEditing && (
                <button
                  onClick={() => {
                    // 如果提供了链接选择器回调，优先使用它
                    if (onOpenLinkSelector) {
                      onOpenLinkSelector()
                      return
                    }
                    // 否则使用原来的输入框方式
                    if (!onShareLinkChange && shareLinkNotLoggedInMessage) {
                      alert(shareLinkNotLoggedInMessage)
                      return
                    }
                    setShowLinkPanel(!showLinkPanel)
                  }}
                  className={`px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation ${
                    shareLink ? 'bg-cyan-600 text-white hover:bg-cyan-700 active:bg-cyan-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200'
                  }`}
                  title="设置跳转链接"
                >
                  <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  <span className="hidden sm:inline">链接</span>
                </button>
              )}
              {isEditing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-green-600 text-white hover:bg-green-700 active:bg-green-700 rounded-md transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                    title="保存 (Ctrl+S)"
                  >
                    <svg width="14" height="14" className="sm:w-4 sm:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                      <polyline points="17 21 17 13 7 13 7 21"></polyline>
                      <polyline points="7 3 7 8 15 8"></polyline>
                    </svg>
                    <span className="hidden sm:inline">保存</span>
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200 rounded-md transition-colors touch-manipulation"
                    title="取消 (ESC)"
                  >
                    取消
                  </button>
                </>
              ) : (
                <button
                  onClick={onClose}
                  className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-200 rounded-md transition-colors touch-manipulation"
                  title="关闭 (ESC)"
                >
                  关闭
                </button>
              )}
            </div>
          </div>

          {/* 字体和字体大小设置 */}
          {isEditing && (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">字体</label>
                <select
                  value={currentFontFamily}
                  onChange={(e) => setCurrentFontFamily(e.target.value)}
                  className="w-full px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ fontFamily: 'inherit' }}
                >
                  {fontOptions.map((font) => (
                    <option key={font.value} value={font.value}>
                      {font.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1 sm:mb-2">
                  <label className="text-xs sm:text-sm font-medium text-gray-700">字体大小</label>
                  <span className="text-xs sm:text-sm text-gray-600">{currentFontSize}px</span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="72"
                  value={currentFontSize}
                  onChange={(e) => setCurrentFontSize(Number(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1">
                  <span>10px</span>
                  <span>72px</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 分享链接输入面板 */}
        {showLinkPanel && showShareLinkInput && !isEditing && (
          <div className="px-3 sm:px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-2">设置跳转链接</div>
            <p className="text-xs text-gray-500 mb-3">
              输入分享链接地址，当此画布被分享时，点击此文本将跳转到链接的画布
            </p>
            <input
              type="text"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="例如: /share/abc123 或完整URL"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 mb-3"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveShareLink()
                } else if (e.key === 'Escape') {
                  setShowLinkPanel(false)
                }
              }}
            />
            <div className="flex gap-2 items-center">
              {/* 清除链接放最左边 */}
              {shareLink && (
                <button
                  onClick={handleClearShareLink}
                  className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                >
                  清除链接
                </button>
              )}
              {/* 已设置链接时显示打开链接 */}
              {shareLink && (
                <a
                  href={shareLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 text-sm text-cyan-600 hover:text-cyan-700 hover:underline transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  打开链接 ↗
                </a>
              )}
              <div className="flex-1" />
              <button
                onClick={() => setShowLinkPanel(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveShareLink}
                className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded transition-colors"
              >
                保存
              </button>
            </div>
          </div>
        )}

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-6 min-h-0" style={{
          transform: `rotate(${rotation}deg)`,
          transition: 'transform 0.3s ease',
          transformOrigin: 'center center'
        }}>
          {isEditing ? (
            <textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              className="w-full h-full resize-none border border-gray-300 rounded-md p-3 sm:p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-800 text-sm sm:text-base"
              style={{
                fontSize: `${currentFontSize}px`,
                fontFamily: currentFontFamily,
                lineHeight: 1.8
              }}
              placeholder="请输入文本内容..."
              autoFocus
            />
          ) : (
            <div
              className="whitespace-pre-wrap break-words text-gray-800 text-sm sm:text-base"
              style={{
                fontSize: `${currentFontSize}px`,
                fontFamily: currentFontFamily,
                lineHeight: 1.8
              }}
            >
              {textContent || '(空内容)'}
            </div>
          )}
        </div>

        {/* 底部提示 */}
        <div className="flex-shrink-0 px-3 sm:px-6 py-2 sm:py-3 border-t border-gray-200 bg-gray-50">
          <p className="text-[10px] sm:text-xs text-gray-500 text-center">
            {isEditing 
              ? <span className="hidden sm:inline">Ctrl+S 保存 | ESC 取消</span>
              : <span className="hidden sm:inline">{hasPrev || hasNext ? '← → 切换 | ' : ''}T 旋转 | ESC 键关闭 | 点击外部区域关闭</span>
            }
          </p>
        </div>
      </div>
    </div>
  )
}

export default TextViewerDialog

